import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import VideoCard from "@/components/feature/VideoCard";
import { videos } from "@/mocks/videos";
import { searchVideos, sortVideos, type SortKey } from "@/utils/video";
import { usePageMeta } from "@/hooks/usePageMeta";

const hotKeywords = ["搞笑合集", "纪录片", "现场音乐", "综艺名场面", "美食教学", "篮球集锦"];

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "latest", label: "最新上传" },
  { key: "hot", label: "最热观看" },
];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") || "";
  const [input, setInput] = useState(query);
  const [sort, setSort] = useState<SortKey>("latest");

  usePageMeta(
    query ? `“${query}”的搜索结果 - 黄色免费视频` : "搜索视频 - 黄色免费视频",
    query
      ? `黄色免费视频中与“${query}”相关的免费视频搜索结果，支持按最新与最热排序。`
      : "输入关键词搜索黄色免费视频站内的免费视频资源。",
    "视频搜索,免费视频,在线搜索,黄色免费视频",
  );

  useEffect(() => {
    setInput(query);
    setSort("latest");
    window.scrollTo({ top: 0 });
  }, [query]);

  const results = useMemo(() => {
    const matched = searchVideos(videos, query);
    return sortVideos(matched, sort);
  }, [query, sort]);

  const hotVideos = useMemo(() => sortVideos(videos, "hot").slice(0, 4), []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setParams(q ? { q } : {});
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full px-4 md:px-6 pt-6 md:pt-8">
          <nav className="flex items-center gap-1.5 text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors">
              首页
            </Link>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line" />
            </span>
            <span className="text-foreground-800 font-medium">搜索结果</span>
          </nav>

          <h1 className="mt-4 font-heading text-2xl md:text-3xl font-extrabold text-foreground-950">
            搜索结果
          </h1>

          <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-2 max-w-2xl">
            <div className="relative flex-1">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base" />
              </span>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="搜索视频标题或简介"
                className="w-full h-12 pl-11 pr-4 text-sm bg-background-100 border border-background-200 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50"
              />
            </div>
            <button
              type="submit"
              className="h-12 px-7 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500">热门搜索：</span>
            {hotKeywords.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setParams({ q: k })}
                className="px-2.5 py-1 text-xs rounded-full bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
              >
                {k}
              </button>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-b border-background-200 pb-4">
            <p className="text-sm text-foreground-600">
              {query ? (
                <>
                  关键词 <span className="font-semibold text-foreground-950">“{query}”</span>{" "}
                  共找到 <span className="font-semibold text-primary-600">{results.length}</span> 个视频
                </>
              ) : (
                "输入关键词开始搜索视频"
              )}
            </p>
            {results.length > 0 && (
              <div className="flex items-center gap-2">
                {sortOptions.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => setSort(o.key)}
                    className={`px-3 py-1.5 text-xs rounded-full border transition-colors whitespace-nowrap cursor-pointer ${
                      sort === o.key
                        ? "bg-secondary-900 border-secondary-900 text-background-50"
                        : "bg-background-50 border-background-300 text-foreground-700 hover:border-secondary-400"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-8">
          {results.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {results.map((v) => (
                <VideoCard key={v.id} video={v} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center text-center py-14">
              <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center text-foreground-400 mb-4">
                <i className="ri-emotion-sad-line text-3xl" />
              </span>
              <h2 className="font-heading text-xl font-bold text-foreground-950">
                {query ? "没有找到相关视频" : "请输入搜索关键词"}
              </h2>
              <p className="mt-2 text-sm text-foreground-600 max-w-md">
                换个关键词试试，或者看看下面这些大家都在看的热门视频。
              </p>

              <div className="mt-8 w-full">
                <h3 className="font-heading text-base font-bold text-foreground-950 mb-4">
                  热门视频推荐
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                  {hotVideos.map((v) => (
                    <VideoCard key={v.id} video={v} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}